import { systemVersionLabel } from '../features/users/utils/systemVersion';
import { formatUserMoney, parseUserAmountMinor, userMarketLabel, userAmountPlaceholder, userAmountError } from '../features/users/utils/userMoney';
import type { UserMoneyMetadata } from '../features/users/types/users.types';
import { useState, useCallback, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../components/feedback/ConfirmDialog';
import {
  useAdminUserDetail,
  USER_ROLE_LABELS,
  USER_STATUS_LABELS,
  APPLE_VERIFICATION_LABELS,
} from '../features/users';
import type {
  UserStatus,
  AppleVerificationStatus,
  AdminWalletAdjustmentType,
  AdminFinanceComponentDTO,
  AdminWalletTransactionDTO,
  AdminWalletTransactionType,
} from '../features/users';
import * as usersService from '../features/users/services/users.service';
import { formatDateTime } from '../lib/format';

const STATUS_STYLES: Record<UserStatus, string> = {
  active: 'bg-green-50 text-green-700',
  disabled: 'bg-red-50 text-red-700',
};

const APPLE_STATUS_STYLES: Record<Exclude<AppleVerificationStatus, ''>, string> = {
  verified: 'bg-sky-50 text-sky-700',
  revoked: 'bg-amber-50 text-amber-700',
};

const WALLET_TRANSACTION_TYPE_LABELS: Record<Exclude<AdminWalletTransactionType, ''>, string> = {
  task_reward: '任务奖励',
  commission_fixed: '固定返佣',
  commission_percentage: '比例返佣',
  withdrawal_freeze: '提现冻结',
  withdrawal_refund: '提现退回',
  withdrawal_settle: '提现结算',
  admin_credit: '后台加款',
  admin_debit: '后台扣款',
};

const WALLET_TRANSACTION_TYPE_OPTIONS: { value: AdminWalletTransactionType; label: string }[] = [
  { value: '', label: '全部类型' },
  { value: 'task_reward', label: '任务奖励' },
  { value: 'commission_fixed', label: '固定返佣' },
  { value: 'commission_percentage', label: '比例返佣' },
  { value: 'withdrawal_freeze', label: '提现冻结' },
  { value: 'withdrawal_refund', label: '提现退回' },
  { value: 'withdrawal_settle', label: '提现结算' },
  { value: 'admin_credit', label: '后台加款' },
  { value: 'admin_debit', label: '后台扣款' },
];

const WALLET_TRANSACTION_PAGE_SIZE = 10;

type DialogState =
  | { type: 'closed' }
  | { type: 'active' }
  | { type: 'disabled' };

type WalletAdjustmentDialogState =
  | { type: 'closed' }
  | { type: AdminWalletAdjustmentType };

interface EditFormState {
  email: string;
  nickname: string;
  password: string;
  status: UserStatus;
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</div>
      <div className="mt-3 text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
      {hint && <div className="mt-2 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start py-2 border-b border-gray-100 last:border-b-0">
      <span className="w-32 shrink-0 text-xs text-gray-500">{label}</span>
      <span className="text-sm text-gray-900 break-all">{value || '-'}</span>
    </div>
  );
}

function financeComponentDetailURL(item: AdminFinanceComponentDTO, userId: number): string | null {
  switch (item.key) {
    case 'task_reward':
      return `/rewards?userId=${userId}`;
    case 'commission_fixed':
      return `/commissions?beneficiaryUserId=${userId}&type=fixed`;
    case 'commission_percentage':
      return `/commissions?beneficiaryUserId=${userId}&type=percentage`;
    case 'withdrawal_freeze':
    case 'withdrawal_refund':
    case 'withdrawal_settle':
      return `/withdrawals?userId=${userId}`;
    default:
      return null;
  }
}

function FinanceComponentCard({
  item,
  userId,
  onSelectWalletType,
  money,
}: {
  money: UserMoneyMetadata;
  item: AdminFinanceComponentDTO;
  userId: number;
  onSelectWalletType: (type: Exclude<AdminWalletTransactionType, ''>) => void;
}) {
  const detailURL = financeComponentDetailURL(item, userId);
  const amountClass = item.amount_fen < 0
    ? 'text-red-600'
    : item.amount_fen > 0 && item.direction !== 'snapshot'
      ? 'text-green-600'
      : 'text-gray-900';

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium text-gray-500">{item.label}</div>
          <div className={`mt-2 text-lg font-semibold tabular-nums ${amountClass}`}>
            {formatUserMoney(item.amount_minor ?? item.amount_fen, money, item.direction !== 'snapshot')}
          </div>
          <div className="mt-1 text-xs text-gray-400">
            {item.detail_kind === 'wallet_transaction' ? `${item.count} 笔流水` : '钱包快照'}
          </div>
        </div>
        {item.wallet_transaction_type ? (
          <button
            type="button"
            onClick={() => onSelectWalletType(item.wallet_transaction_type!)}
            className="shrink-0 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-100"
          >
            流水
          </button>
        ) : null}
      </div>
      {detailURL ? (
        <Link to={detailURL} className="mt-3 inline-flex text-xs font-medium text-primary hover:underline">
          业务明细
        </Link>
      ) : null}
    </div>
  );
}

function WalletAdjustmentDialog({
  open,
  money,
  type,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  money: UserMoneyMetadata;
  type: AdminWalletAdjustmentType;
  loading: boolean;
  onConfirm: (amountMinor: number, remark: string) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setAmount('');
      setRemark('');
      setError('');
    }
  }, [open]);

  const handleConfirm = useCallback(async () => {
    const amountMinor = parseUserAmountMinor(amount, money);
    if (!amountMinor || amountMinor <= 0) {
      setError(userAmountError(money));
      return;
    }
    const trimmedRemark = remark.trim();
    if (!trimmedRemark) {
      setError('请填写备注');
      return;
    }
    setError('');
    const ok = await onConfirm(amountMinor, trimmedRemark);
    if (ok) {
      setAmount('');
      setRemark('');
    }
  }, [amount, onConfirm, remark, money]);

  if (!open) return null;

  const isDebit = type === 'debit';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={loading ? undefined : onCancel} />
      <div className="relative mx-4 w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
        <h3 className="text-base font-medium text-gray-900">
          {isDebit ? '扣款' : '加款'}
        </h3>
        <div className="mt-4 space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">金额（{money.currency}）</label>
            <input
              type="text"
              inputMode={money.currency_exponent === 0 ? 'numeric' : 'decimal'}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder={userAmountPlaceholder(money)}
              className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">备注</label>
            <textarea
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              rows={3}
              placeholder="请输入调整原因"
              className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40"
          >
            取消
          </button>
          <button
            onClick={() => void handleConfirm()}
            disabled={loading}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm text-white transition-colors disabled:opacity-40 ${
              isDebit ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {loading && <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
            确认{isDebit ? '扣款' : '加款'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const { data, loading, error, updating, reload, updateStatus, updateUser, adjustWallet, releaseAppleVerification } = useAdminUserDetail(userId);
  const [dialog, setDialog] = useState<DialogState>({ type: 'closed' });
  const [walletDialog, setWalletDialog] = useState<WalletAdjustmentDialogState>({ type: 'closed' });
  const [releaseAppleDialogOpen, setReleaseAppleDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>({
    email: '',
    nickname: '',
    password: '',
    status: 'active',
  });
  const [transactionItems, setTransactionItems] = useState<AdminWalletTransactionDTO[]>([]);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionType, setTransactionType] = useState<AdminWalletTransactionType>('');
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');

  useEffect(() => {
    if (!data) return;
    setEditForm({
      email: data.user.email || '',
      nickname: data.user.nickname || '',
      password: '',
      status: data.user.status,
    });
  }, [data]);

  const handleConfirm = useCallback(async (note: string) => {
    if (dialog.type === 'closed') return;
    const ok = await updateStatus(dialog.type, note);
    if (ok) {
      setDialog({ type: 'closed' });
    }
  }, [dialog.type, updateStatus]);

  const handleSaveProfile = useCallback(async () => {
    const ok = await updateUser({
      email: editForm.email.trim(),
      nickname: editForm.nickname.trim(),
      password: editForm.password.trim() || undefined,
      status: editForm.status,
    });
    if (ok) {
      setEditing(false);
      setEditForm((current) => ({ ...current, password: '' }));
    }
  }, [editForm, updateUser]);

  const loadWalletTransactions = useCallback(async () => {
    if (!Number.isFinite(userId) || userId <= 0 || data?.user.role !== 'user') {
      setTransactionItems([]);
      setTransactionTotal(0);
      return;
    }
    setTransactionsLoading(true);
    setTransactionsError('');
    try {
      const result = await usersService.listAdminUserWalletTransactions(userId, {
        page: transactionPage,
        pageSize: WALLET_TRANSACTION_PAGE_SIZE,
        type: transactionType,
      });
      setTransactionItems(result.items);
      setTransactionTotal(result.total);
    } catch {
      setTransactionsError('余额流水加载失败');
    } finally {
      setTransactionsLoading(false);
    }
  }, [data?.user.role, transactionPage, transactionType, userId]);

  useEffect(() => {
    void loadWalletTransactions();
  }, [loadWalletTransactions]);

  const handleAdjustWallet = useCallback(async (amountMinor: number, remark: string) => {
    if (walletDialog.type === 'closed') return false;
    const ok = await adjustWallet({
      type: walletDialog.type,
      amountMinor,
      remark,
    });
    if (ok) {
      setWalletDialog({ type: 'closed' });
      await loadWalletTransactions();
    }
    return ok;
  }, [adjustWallet, loadWalletTransactions, walletDialog.type]);

  const handleReleaseAppleVerification = useCallback(async () => {
    const ok = await releaseAppleVerification();
    if (ok) {
      setReleaseAppleDialogOpen(false);
    }
  }, [releaseAppleVerification]);

  const handleSelectFinanceWalletType = useCallback((type: Exclude<AdminWalletTransactionType, ''>) => {
    setTransactionType(type);
    setTransactionPage(1);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-red-500 mb-3">{error ?? '用户不存在'}</p>
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => void reload()} className="text-sm text-primary hover:underline">
            重试
          </button>
          <Link to="/users" className="text-sm text-gray-500 hover:text-gray-700">
            返回用户列表
          </Link>
        </div>
      </div>
    );
  }

  const user = data.user;
  const currentStatus = user.status;
  const nextStatus = currentStatus === 'active' ? 'disabled' : 'active';
  const transactionTotalPages = Math.max(1, Math.ceil(transactionTotal / WALLET_TRANSACTION_PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Link to="/users" className="hover:text-primary transition-colors">用户管理</Link>
        <span>/</span>
        <span className="text-gray-900">用户详情</span>
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-semibold text-gray-900">
                {user.nickname || '-'}
              </h2>
              <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[currentStatus]}`}>
                {USER_STATUS_LABELS[currentStatus]}
              </span>
            </div>
            <p className="mt-2 text-sm text-gray-500 break-all">
              #{user.id} · {userMarketLabel(user)} · {user.email || '-'}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              注册于 {formatDateTime(user.created_at)}，最近更新 {formatDateTime(user.updated_at)}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setDialog({ type: nextStatus })}
              disabled={updating}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                nextStatus === 'disabled'
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-primary text-white hover:bg-primary/90'
              } disabled:opacity-50`}
            >
              {nextStatus === 'disabled' ? '禁用账号' : '恢复账号'}
            </button>
            <button
              onClick={() => void reload()}
              className="px-4 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition-colors"
            >
              刷新
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard label="累计奖励" value={formatUserMoney(data.summary.settled_reward_amount_minor ?? data.summary.settled_reward_amount_fen, user)} hint={`${data.summary.reward_count} 笔`} />
        <StatCard label="累计返佣" value={formatUserMoney(data.summary.settled_commission_amount_minor ?? data.summary.settled_commission_amount_fen, user)} hint={`${data.summary.commission_count} 笔`} />
        <StatCard label="提现记录" value={data.summary.withdrawal_count} hint={`待处理 ${data.summary.pending_withdrawal_count}`} />
        <StatCard label="任务审核" value={data.summary.assignment_count} hint={`待审 ${data.summary.pending_assignment_review_count}`} />
        <StatCard label="风险命中" value={data.risk.risk_hit_count} hint={`客服反馈 ${data.summary.support_feedback_count}`} />
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-medium text-gray-900">基础资料</h3>
              {user.role === 'user' ? (
                <button
                  type="button"
                  onClick={() => setEditing((value) => !value)}
                  className="text-sm text-primary hover:underline"
                >
                  {editing ? '取消编辑' : '编辑资料'}
                </button>
              ) : null}
            </div>
            {editing ? (
              <div className="mt-4 space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(event) => setEditForm((current) => ({ ...current, email: event.target.value }))}
                    placeholder="邮箱"
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <input
                    type="text"
                    value={editForm.nickname}
                    onChange={(event) => setEditForm((current) => ({ ...current, nickname: event.target.value }))}
                    placeholder="昵称"
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <input
                    type="password"
                    value={editForm.password}
                    onChange={(event) => setEditForm((current) => ({ ...current, password: event.target.value }))}
                    placeholder="新密码，不改留空"
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <select
                    value={editForm.status}
                    onChange={(event) => setEditForm((current) => ({ ...current, status: event.target.value as UserStatus }))}
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="active">正常</option>
                    <option value="disabled">已禁用</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleSaveProfile}
                  disabled={updating}
                  className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  保存资料
                </button>
              </div>
            ) : (
              <div className="mt-4">
                <InfoRow label="用户 ID" value={String(user.id)} />
                <InfoRow label="邮箱" value={user.email || '-'} />
                <InfoRow label="昵称" value={user.nickname || '-'} />
                <InfoRow label="角色" value={USER_ROLE_LABELS[user.role]} />
                <InfoRow label="邀请码" value={user.invite_code || '-'} />
                <InfoRow
                  label="邀请人"
                  value={data.direct_inviter ? `${data.direct_inviter.nickname || '-'} (#${data.direct_inviter.id}) ${data.direct_inviter.email || ''}` : '未绑定'}
                />
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-medium text-gray-900">钱包概览</h3>
              {user.role === 'user' ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setWalletDialog({ type: 'credit' })}
                    disabled={updating}
                    className="h-8 rounded-md bg-primary px-3 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    加款
                  </button>
                  <button
                    type="button"
                    onClick={() => setWalletDialog({ type: 'debit' })}
                    disabled={updating}
                    className="h-8 rounded-md border border-red-200 px-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                  >
                    扣款
                  </button>
                </div>
              ) : null}
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <StatCard label="可用余额" value={formatUserMoney(data.wallet?.balance_minor ?? data.wallet?.balance_fen ?? 0, user)} />
              <StatCard label="冻结金额" value={formatUserMoney(data.wallet?.frozen_minor ?? data.wallet?.frozen_fen ?? 0, user)} />
              <StatCard label="累计收益" value={formatUserMoney(data.wallet?.total_earned_minor ?? data.wallet?.total_earned_fen ?? 0, user)} />
              <StatCard label="累计提现" value={formatUserMoney(data.wallet?.total_withdrawn_minor ?? data.wallet?.total_withdrawn_fen ?? 0, user)} />
            </div>
          </div>

          {user.role === 'user' && data.finance_analysis ? (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h3 className="text-base font-medium text-gray-900">财务分析</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    流水净额 {formatUserMoney(data.finance_analysis.transaction_net_minor ?? data.finance_analysis.transaction_net_fen, user)}，入账 {formatUserMoney(data.finance_analysis.transaction_income_minor ?? data.finance_analysis.transaction_income_fen, user)}，出账 {formatUserMoney(data.finance_analysis.transaction_expense_minor ?? data.finance_analysis.transaction_expense_fen, user)}
                  </p>
                </div>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                  data.finance_analysis.balance_delta_fen === 0
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  余额差异 {formatUserMoney(data.finance_analysis.balance_delta_minor ?? data.finance_analysis.balance_delta_fen, user)}
                </span>
              </div>
              {data.finance_analysis.balance_delta_fen !== 0 ? (
                <div className="mt-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                  当前可用余额与钱包流水净额不一致，请结合下方流水、奖励、返佣、提现和后台调整记录核对。
                </div>
              ) : null}
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {data.finance_analysis.components.map((item) => (
                  <FinanceComponentCard
                    key={item.key}
                    item={item}
                    money={user}
                    userId={user.id}
                    onSelectWalletType={handleSelectFinanceWalletType}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {user.role === 'user' ? (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-base font-medium text-gray-900">余额流水</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={transactionType}
                    onChange={(event) => {
                      setTransactionType(event.target.value as AdminWalletTransactionType);
                      setTransactionPage(1);
                    }}
                    className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    {WALLET_TRANSACTION_TYPE_OPTIONS.map((option) => (
                      <option key={option.value || 'all'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => void loadWalletTransactions()}
                    disabled={transactionsLoading}
                    className="h-8 rounded-md border border-gray-300 px-3 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                  >
                    刷新
                  </button>
                </div>
              </div>

              {transactionsError ? (
                <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
                  {transactionsError}
                </div>
              ) : null}

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">时间</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">类型</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">变动金额</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">变动后余额</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">关联</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">备注</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactionsLoading ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                          加载中...
                        </td>
                      </tr>
                    ) : transactionItems.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                          暂无余额流水
                        </td>
                      </tr>
                    ) : (
                      transactionItems.map((item) => (
                        <tr key={item.id} className="border-b border-gray-100 last:border-b-0">
                          <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                            {formatDateTime(item.created_at)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-gray-700">
                            {WALLET_TRANSACTION_TYPE_LABELS[item.type] ?? item.type}
                          </td>
                          <td className={`whitespace-nowrap px-3 py-3 text-right font-medium tabular-nums ${
                            item.amount_fen < 0 ? 'text-red-600' : item.amount_fen > 0 ? 'text-green-600' : 'text-gray-700'
                          }`}>
                            {formatUserMoney(item.amount_minor ?? item.amount_fen, item, true)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-900">
                            {formatUserMoney(item.balance_after_minor ?? item.balance_after_fen, item)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                            {item.ref_order_type ? `${item.ref_order_type} #${item.ref_order_id ?? '-'}` : '-'}
                          </td>
                          <td className="min-w-48 px-3 py-3 text-gray-600">
                            <span className="line-clamp-2 break-words">{item.remark || '-'}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-col gap-3 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                <span>共 {transactionTotal} 条</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTransactionPage((page) => Math.max(1, page - 1))}
                    disabled={transactionPage <= 1 || transactionsLoading}
                    className="rounded border border-gray-300 px-3 py-1 text-sm transition-colors hover:bg-gray-50 disabled:opacity-40"
                  >
                    上一页
                  </button>
                  <span className="tabular-nums">{transactionPage} / {transactionTotalPages}</span>
                  <button
                    type="button"
                    onClick={() => setTransactionPage((page) => Math.min(transactionTotalPages, page + 1))}
                    disabled={transactionPage >= transactionTotalPages || transactionsLoading}
                    className="rounded border border-gray-300 px-3 py-1 text-sm transition-colors hover:bg-gray-50 disabled:opacity-40"
                  >
                    下一页
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-base font-medium text-gray-900">邀请与团队</h3>
            {data.team_stats ? (
              <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <StatCard label="直邀人数" value={data.team_stats.direct_invite_count} />
                <StatCard label="团队人数" value={data.team_stats.team_count} />
                <StatCard label="一级返佣" value={formatUserMoney(data.team_stats.level1_commission_minor ?? data.team_stats.level1_commission_fen, user)} />
                <StatCard label="总返佣" value={formatUserMoney(data.team_stats.total_commission_minor ?? data.team_stats.total_commission_fen, user)} />
              </div>
            ) : (
              <div className="mt-4 text-sm text-gray-400">暂无团队统计数据</div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-medium text-gray-900">Apple 验证</h3>
              {user.role === 'user' && data.apple_verification ? (
                <button
                  type="button"
                  onClick={() => setReleaseAppleDialogOpen(true)}
                  disabled={updating}
                  className="text-sm text-red-600 transition-colors hover:text-red-700 disabled:opacity-50"
                >
                  解除绑定
                </button>
              ) : null}
            </div>
            {data.apple_verification ? (
              <div className="mt-4 space-y-2">
                <div>
                  <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${
                    APPLE_STATUS_STYLES[data.apple_verification.verification_status as Exclude<AppleVerificationStatus, ''>] ?? 'bg-gray-100 text-gray-600'
                  }`}>
                    {APPLE_VERIFICATION_LABELS[data.apple_verification.verification_status as Exclude<AppleVerificationStatus, ''>]}
                  </span>
                </div>
                <InfoRow label="账号" value={data.apple_verification.apple_account} />
                <InfoRow label="脱敏账号" value={data.apple_verification.masked_account || '-'} />
                <InfoRow label="StoreFront" value={data.apple_verification.store_front || '-'} />
                <InfoRow label="验证时间" value={data.apple_verification.verified_at ? formatDateTime(data.apple_verification.verified_at) : '-'} />
                <InfoRow label="撤销时间" value={data.apple_verification.revoked_at ? formatDateTime(data.apple_verification.revoked_at) : '-'} />
              </div>
            ) : (
              <div className="mt-4 text-sm text-gray-400">暂未上报 Apple 验证信息</div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-base font-medium text-gray-900">风险概览</h3>
            <div className="mt-4 space-y-2">
              <InfoRow label="命中次数" value={String(data.risk.risk_hit_count)} />
              <InfoRow label="设备数" value={String(data.risk.unique_device_count)} />
              <InfoRow label="Apple 邮箱数" value={String(data.risk.unique_apple_email_count)} />
              <InfoRow label="最近设备 GUID" value={data.risk.latest_device_guid || '-'} />
              <InfoRow label="最近 Apple 邮箱" value={data.risk.latest_apple_email || '-'} />
              <InfoRow label="最近包标识" value={data.risk.latest_bundle_id || '-'} />
              <InfoRow label="最近命中时间" value={data.risk.latest_risk_at ? formatDateTime(data.risk.latest_risk_at) : '-'} />
            </div>
            {data.risk.reason_breakdown.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {data.risk.reason_breakdown.map((item) => (
                  <span key={item.code} className="inline-flex rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                    {item.code} · {item.count}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-base font-medium text-gray-900">登录设备</h3>
            <div className="mt-4 space-y-2">
              <InfoRow label="设备数" value={String(data.device.unique_device_count)} />
              <InfoRow label="Apple 邮箱数" value={String(data.device.unique_apple_email_count)} />
              <InfoRow label="最近设备 GUID" value={data.device.latest_device_guid || '-'} />
              <InfoRow label="最近 Apple 邮箱" value={data.device.latest_apple_email || '-'} />
              <InfoRow label="最近包标识" value={data.device.latest_bundle_id || '-'} />
              <InfoRow label="系统版本" value={systemVersionLabel(data.system_version)} />
              <InfoRow label="系统版本上报时间" value={data.system_version?.reported_at ? formatDateTime(data.system_version.reported_at) : '-'} />
              <InfoRow label="最近 App 版本" value={data.device.latest_app_version || '-'} />
              <InfoRow label="最近活跃时间" value={data.device.latest_seen_at ? formatDateTime(data.device.latest_seen_at) : '-'} />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-base font-medium text-gray-900">联查入口</h3>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <Link to={`/team?userId=${user.id}`} className="text-primary hover:underline">团队查询</Link>
              <Link to={`/rewards?userId=${user.id}`} className="text-primary hover:underline">奖励记录</Link>
              <Link to={`/commissions?beneficiaryUserId=${user.id}`} className="text-primary hover:underline">返佣记录</Link>
              <Link to={`/withdrawals?userId=${user.id}`} className="text-primary hover:underline">提现管理</Link>
              <Link to={`/withdrawal-accounts?userId=${user.id}`} className="text-primary hover:underline">提现账号管理</Link>
              <Link to={`/assignments?userId=${user.id}`} className="text-primary hover:underline">任务审核</Link>
              <Link to={`/support-feedback?userId=${user.id}`} className="text-primary hover:underline">客服反馈</Link>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <h3 className="text-base font-medium text-gray-900">最近状态操作</h3>
            {data.recent_status_changes.length === 0 ? (
              <div className="mt-4 text-sm text-gray-400">暂无状态操作记录</div>
            ) : (
              <div className="mt-4 space-y-3">
                {data.recent_status_changes.map((item) => (
                  <div key={item.id} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm text-gray-900">
                        {USER_STATUS_LABELS[item.from_status]} → {USER_STATUS_LABELS[item.to_status]}
                      </div>
                      <div className="text-xs text-gray-400">{formatDateTime(item.created_at)}</div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      操作人：{item.operator ? `${item.operator.nickname || '-'} (#${item.operator.id})` : `#${item.operator_id}`}
                    </div>
                    <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">{item.reason || '-'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <ConfirmDialog
        open={dialog.type !== 'closed'}
        title={dialog.type === 'disabled' ? '确认禁用账号' : '确认恢复账号'}
        description={dialog.type === 'disabled' ? '禁用后该用户将无法正常登录。' : '恢复后该用户将重新可以正常登录。'}
        confirmLabel={dialog.type === 'disabled' ? '确认禁用' : '确认恢复'}
        variant={dialog.type === 'disabled' ? 'danger' : 'primary'}
        loading={updating}
        noteLabel="变更原因"
        noteRequired
        onConfirm={handleConfirm}
        onCancel={() => setDialog({ type: 'closed' })}
      />
      <WalletAdjustmentDialog
        money={user}
        open={walletDialog.type !== 'closed'}
        type={walletDialog.type === 'closed' ? 'credit' : walletDialog.type}
        loading={updating}
        onConfirm={handleAdjustWallet}
        onCancel={() => setWalletDialog({ type: 'closed' })}
      />
      <ConfirmDialog
        open={releaseAppleDialogOpen}
        title="确认解除 Apple ID 绑定"
        description="解除后该账号当前的 Apple 验证记录会被删除，原 Apple ID 可重新绑定到其他账号。"
        confirmLabel="确认解除"
        variant="danger"
        loading={updating}
        onConfirm={() => void handleReleaseAppleVerification()}
        onCancel={() => setReleaseAppleDialogOpen(false)}
      />
    </div>
  );
}
